import { useAppSelector } from 'hooks/redux';
import React, { useMemo } from 'react';

import Typography from '@mui/material/Typography';
import { categoriesSelectors } from 'app-components/flux/categories';
import { useField } from 'app-components/flux/categories/categoryFormHooks';
import { chartOfAccountsTypes } from 'app-components/flux/chart-of-accounts';
import { type ChartOfAccountProps, CoaTypeEnum } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { entitlementsSelectors } from 'app-components/flux/entitlements';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import CategoryField from 'components/QuickenControls/CategoryField';
import { getCategoryFieldMenuDepth, getDepthOfCategory, getMaxDepthOfDescendants } from './utils';

const CategoryParent = () => {
  const { value: originalId } = useField('id');
  const { value, onBlur, onValueChange, error, helperText, disabled } = useField('parent');
  const maxCategoryDepth = useAppSelector(
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'maxCategoryDepth') || 3,
  );
  const businessEnabled = useAppSelector(entitlementsSelectors.getBusinessEnabled);
  const enableUsageTypes = useAppSelector(categoriesSelectors.enableCategoryUsageTypes);
  const coaNodesById = useAppSelector(categoriesSelectors.getCategoriesById);
  const categoriesByParentId = useAppSelector(categoriesSelectors.getCategoriesByParentId);
  const atMaxDepth = originalId
    ? getMaxDepthOfDescendants(coaNodesById.get(originalId), categoriesByParentId) + 1 >= maxCategoryDepth
    : false;

  const filterFn = useMemo(() => {
    let maxDepthOfChildren = 0; // depth from the current node to the furthest leaf
    if (originalId) {
      const coaNode = coaNodesById.get(originalId);
      maxDepthOfChildren = getMaxDepthOfDescendants(coaNode, categoriesByParentId);
    }
    return (c: { type: string; id: string }) => {
      let totalDepth = 0; // depth from the current COA to the furthest leaf if moved

      const coaNode = coaNodesById.get(c.id);
      if (coaNode?.parentId) {
        const depthOfCurrent = getDepthOfCategory(coaNode, coaNodesById);
        totalDepth = depthOfCurrent + maxDepthOfChildren;
      }

      return (
        c.id !== originalId &&
        c.type !== CoaTypeEnum.ACCOUNT &&
        c.type !== CoaTypeEnum.BALANCE_ADJUSTMENT &&
        c.type !== CoaTypeEnum.UNCATEGORIZED &&
        totalDepth < maxCategoryDepth
      ); // filter out any matches that would exceed maxCategoryDepth
    };
  }, [categoriesByParentId, coaNodesById, maxCategoryDepth, originalId]);

  return (
    <>
      <CategoryField
        error={!!error}
        helperText={helperText}
        label='Subcategory Of'
        filterFn={filterFn}
        onBlur={onBlur}
        onChange={(value: ChartOfAccountProps) => onValueChange(value.id)}
        textFieldVariant='filled'
        value={{ type: chartOfAccountsTypes.CoaTypeEnum.CATEGORY, id: value || '0' }}
        editable
        allowNone
        maxCategoryDepth={Number(maxCategoryDepth)}
        disabled={disabled || atMaxDepth}
        depth={getCategoryFieldMenuDepth(businessEnabled, enableUsageTypes)}
      />
      {atMaxDepth && (
        <Typography sx={{ marginTop: '8px !important' }} variant='captionRegular'>
          This category cannot be a subcategory because it contains too many subcategories.
        </Typography>
      )}
    </>
  );
};

export default CategoryParent;
